import type { GraphMode } from './commonTypes';

type ShapeableNode = {
	style?: 'rect';
	label?: string | null;
	src?: string;
	isVideo?: boolean;
	pdfAnalysisId?: string;
	positioningOutput?: unknown;
};

/**
 * A label is "long" (phrase/concept card) when it has more than two words
 * or more than 40 characters. Individual words and short two-word phrases stay circular.
 */
export const isLongNodeLabel = (label?: string | null): boolean => {
	if (!label) return false;
	const trimmed = label.trim();
	if (!trimmed) return false;
	return trimmed.length > 40 || trimmed.split(/\s+/).filter(Boolean).length > 2;
};

export const isSingleWordLabel = (label?: string | null): boolean => {
	if (!label) return true;
	const trimmed = label.trim();
	if (!trimmed) return true;
	return trimmed.split(/\s+/).filter(Boolean).length === 1 && trimmed.length <= 40;
};

const isForcedRectNode = (node: ShapeableNode): boolean =>
	Boolean(node.pdfAnalysisId || (node.positioningOutput && !node.src));

/** Inspire and Synthesise use circular tokens for individual words and cards for phrases. */
export const graphUsesRoundedRectNodes = (graphMode: GraphMode): boolean =>
	graphMode === 'inspire' || graphMode === 'moodboard';

/** Apply a board mode's default shape without overriding Ideate's node language. */
export const applyDefaultGraphNodeShape = <T extends ShapeableNode>(
	graphMode: GraphMode,
	node: T
): T => {
	if (!graphUsesRoundedRectNodes(graphMode) || isForcedRectNode(node)) {
		if (isForcedRectNode(node)) node.style = 'rect';
		return node;
	}
	// Image and video share rounded media-card chrome (not phrase rects).
	if (node.src) {
		delete node.style;
		return node;
	}
	if (isLongNodeLabel(node.label)) {
		node.style = 'rect';
		return node;
	}
	if (isSingleWordLabel(node.label)) {
		delete node.style;
		return node;
	}
	return node;
};
